import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Wrench, Plus, TestTube, ArrowUpRight, CalendarDays, Users } from "lucide-react";

const STATUS_COLORS = {
  open: "bg-[hsl(32_87%_62%/0.15)] text-[hsl(32_60%_32%)]",
  in_progress: "bg-secondary/15 text-secondary",
  resolved: "bg-primary/10 text-primary",
  closed: "bg-muted text-muted-foreground",
};

const PRIORITY_COLORS = {
  low: "bg-muted text-muted-foreground",
  medium: "bg-secondary/15 text-secondary",
  high: "bg-destructive/15 text-destructive",
};

export default function WorkOrders() {
  const { user } = useAuth();
  const isBoard = user?.role === "admin" || user?.role === "board";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "maintenance", priority: "medium" });
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/work-orders");
    setItems(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/work-orders", form);
      toast.success("Work order submitted");
      setForm({ title: "", description: "", category: "maintenance", priority: "medium" });
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit");
    } finally { setSaving(false); }
  };

  const seedSample = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/demo/seed-work-order");
      toast.success("Sample work order created — opening detail");
      await load();
      window.location.href = `/work-orders/${data.work_order_id}`;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not seed");
    } finally { setSeeding(false); }
  };

  const openItems = items.filter((w) => ["open", "in_progress"].includes(w.status));
  const otherItems = items.filter((w) => !["open", "in_progress"].includes(w.status));

  return (
    <div className="space-y-6" data-testid="work-orders-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-secondary text-sm font-medium mb-2">
            <Wrench className="w-4 h-4" /> Maintenance & projects
          </div>
          <h1 className="text-3xl font-semibold tracking-tight">Work orders</h1>
          <p className="text-foreground/70 mt-1">
            {isBoard
              ? "Open cases with completion meters. Click any card for the full AI-guided workflow."
              : "Track your requests and their progress."}
          </p>
        </div>
        <div className="flex gap-2">
          {isBoard && (
            <Button variant="outline" className="rounded-full h-11" onClick={seedSample} disabled={seeding} data-testid="seed-wo-btn">
              <TestTube className="w-4 h-4 mr-1.5" /> Load sample work order
            </Button>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full h-11" data-testid="new-work-order-btn">
                <Plus className="w-4 h-4 mr-1.5" /> New request
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Submit a work order</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="work-order-form">
                <div>
                  <Label>Title</Label>
                  <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 h-11" data-testid="wo-title-input" />
                </div>
                <div>
                  <Label>Describe the issue</Label>
                  <Textarea required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 min-h-[100px]" data-testid="wo-desc-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Category</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger className="mt-1.5 h-11" data-testid="wo-category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="maintenance">Maintenance</SelectItem>
                        <SelectItem value="landscaping">Landscaping</SelectItem>
                        <SelectItem value="plumbing">Plumbing</SelectItem>
                        <SelectItem value="electrical">Electrical</SelectItem>
                        <SelectItem value="common_area">Common area</SelectItem>
                        <SelectItem value="general">General</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Priority</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                      <SelectTrigger className="mt-1.5 h-11" data-testid="wo-priority-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={saving} className="rounded-full" data-testid="wo-submit-btn">
                    {saving ? "Submitting..." : "Submit request"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary strip */}
      {isBoard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryTile label="Open" value={items.filter((w) => w.status === "open").length} tone="warning" />
          <SummaryTile label="In progress" value={items.filter((w) => w.status === "in_progress").length} tone="secondary" />
          <SummaryTile label="Resolved" value={items.filter((w) => w.status === "resolved").length} tone="primary" />
          <SummaryTile label="Avg completion" value={`${items.length ? Math.round(items.reduce((s, w) => s + (w.completion_pct || 0), 0) / items.length) : 0}%`} tone="primary" />
        </div>
      )}

      {items.length === 0 ? (
        <div className="hoa-card p-12 text-center" data-testid="wo-empty">
          <Wrench className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-50" />
          <div className="text-lg font-semibold mb-1">No work orders yet</div>
          <p className="text-sm text-muted-foreground mb-4">Submit a new request or load the sample to see the full workflow.</p>
          {isBoard && (
            <Button variant="outline" className="rounded-full" onClick={seedSample} disabled={seeding} data-testid="empty-seed-btn">
              <TestTube className="w-4 h-4 mr-1.5" /> Load sample work order
            </Button>
          )}
        </div>
      ) : (
        <>
          <div>
            <h2 className="text-xl font-semibold tracking-tight mb-3">Open cases ({openItems.length})</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {openItems.length === 0 && <div className="hoa-card p-6 text-sm text-muted-foreground text-center col-span-full">No open cases.</div>}
              {openItems.map((w) => <WoCard key={w.id} wo={w} />)}
            </div>
          </div>
          {otherItems.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold tracking-tight mb-3">Closed / resolved</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {otherItems.map((w) => <WoCard key={w.id} wo={w} />)}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SummaryTile({ label, value, tone }) {
  const tones = {
    primary: "text-gold", secondary: "text-secondary", warning: "text-[hsl(32_60%_45%)]",
  };
  return (
    <div className="hoa-card p-4">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-2xl font-mono font-semibold mt-1 ${tones[tone] || "text-foreground"}`}>{value}</div>
    </div>
  );
}

function WoCard({ wo }) {
  const pct = wo.completion_pct || 0;
  return (
    <Link
      to={`/work-orders/${wo.id}`}
      className="hoa-card hoa-card-hover p-5 block group"
      data-testid={`wo-card-${wo.id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex flex-wrap gap-1.5 items-center">
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[wo.status] || STATUS_COLORS.open}`}>{wo.status.replace(/_/g, " ")}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PRIORITY_COLORS[wo.priority] || PRIORITY_COLORS.low}`}>{wo.priority}</span>
          {wo.is_sample && <span className="text-xs bg-gold/25 text-gold px-2 py-0.5 rounded-full font-semibold">SAMPLE</span>}
        </div>
        <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-gold transition-colors" />
      </div>
      <h3 className="font-semibold text-foreground">{wo.title}</h3>
      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{wo.description}</p>

      <div className="mt-4">
        <div className="h-2 rounded-full bg-muted overflow-hidden">
          <div className="h-full bg-gold transition-all" style={{ width: `${pct}%` }} />
        </div>
        <div className="flex items-center justify-between text-xs mt-1">
          <span className="text-muted-foreground flex items-center gap-1"><CalendarDays className="w-3 h-3" /> {wo.start_date ? new Date(wo.start_date).toLocaleDateString() : "—"}</span>
          <span className="font-mono text-gold font-semibold">{pct}%</span>
          <span className="text-muted-foreground">{wo.end_date ? new Date(wo.end_date).toLocaleDateString() : "—"}</span>
        </div>
      </div>

      {wo.assignment?.committee_name && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-2">
          <Users className="w-3 h-3" /> {wo.assignment.committee_name}{wo.assignment.director_name ? ` · ${wo.assignment.director_name}` : ""}
        </div>
      )}
    </Link>
  );
}
